import React, { useState, useEffect, useRef } from "react";
import {
  Box, IconButton, CircularProgress, Typography, Modal,
  useMediaQuery, Button, Snackbar, Alert, LinearProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import FavoriteIcon from "@mui/icons-material/Favorite";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import ShareIcon from "@mui/icons-material/Share";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DownloadForOfflineIcon from "@mui/icons-material/DownloadForOffline";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { useAuth } from "../auth/AuthContext";
import { useApi } from "../api/ApiContext";
import { motion, AnimatePresence } from "framer-motion";

function getImageUrl(p) {
  if (!p) return "";
  return p.startsWith("http") ? p : `https://ksiega.kalmarry.pl/${p.replace(/^\/+/, "")}`;
}
function isVideoFile(p) { return /\.(mp4|webm|mov|avi|mkv|m4v)$/i.test(p || ""); }
function getThumbnailUrl(p) {
  if (!p) return "";
  const base = p.split("/").pop().replace(/\.[^.]+$/, "");
  return `https://ksiega.kalmarry.pl/uploads/thumbs/${base}.jpg`;
}
function getFileExt(p) { const m = (p || "").match(/\.(\w+)(?:\?.*)?$/); return m ? m[1].toLowerCase() : "jpg"; }
async function downloadBlob(url, filename) {
  const resp = await fetch(url, { mode: "cors" });
  const blob = await resp.blob();
  const a = Object.assign(document.createElement("a"), { href: window.URL.createObjectURL(blob), download: filename });
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  window.URL.revokeObjectURL(a.href);
}

const slideVariants = {
  enter: (d) => ({ y: d > 0 ? 500 : -500, opacity: 0 }),
  center: { y: 0, opacity: 1, transition: { duration: 0.28 } },
  exit: (d) => ({ y: d > 0 ? -500 : 500, opacity: 0, transition: { duration: 0.14 } }),
};

const Favorites = () => {
  const { user } = useAuth();
  const api = useApi();
  const isMobile = useMediaQuery("(max-width:900px)");

  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalIdx, setModalIdx] = useState(0);
  const [direction, setDirection] = useState(0);
  const [shareToken, setShareToken] = useState(null);
  const [shareLink, setShareLink] = useState("");
  const [showShareBar, setShowShareBar] = useState(false);
  const [downloadingAll, setDownloadingAll] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [toast, setToast] = useState({ open: false, msg: "", severity: "success" });
  const touchStartY = useRef(null);
  const modalRef = useRef();

  const showToast = (msg, severity = "success") => setToast({ open: true, msg, severity });

  useEffect(() => {
    if (!user) return;
    setLoading(true);
    api.get(`/get_user_images.php?user_id=${user.id}`)
      .then(res => {
        const all = (res.data || []).map(img => ({ ...img, url: getImageUrl(img.image_path) }));
        setImages(all.filter(img => img.liked === 1));
      })
      .catch(() => showToast("Nie udało się załadować.", "error"))
      .finally(() => setLoading(false));
  }, [user?.id, api]); // eslint-disable-line

  useEffect(() => {
    document.body.style.overflow = modalOpen ? "hidden" : "";
    document.body.style.touchAction = modalOpen ? "none" : "";
    return () => { document.body.style.overflow = ""; document.body.style.touchAction = ""; };
  }, [modalOpen]);

  const handleRemoveLike = async (img) => {
    if (!img?.zdjecie_id) return;
    await api.post("/favorite_image.php", { user_id: user.id, image_id: img.zdjecie_id, action: "remove" });
    setImages(imgs => imgs.filter(i => i.zdjecie_id !== img.zdjecie_id));
    if (modalOpen && images.length <= 1) setModalOpen(false);
    showToast("Usunięto z ulubionych");
  };

  const handleDownloadImage = async (img) => {
    if (!img?.image_path) return;
    try { await downloadBlob(getImageUrl(img.image_path), `ulubione_${img.zdjecie_id || Date.now()}.${getFileExt(img.image_path)}`); }
    catch { showToast("Nie udało się pobrać.", "error"); }
  };

  const handleDownloadAll = async () => {
    if (!images.length || downloadingAll) return;
    setDownloadingAll(true); setDownloadProgress(0);
    let done = 0;
    for (const img of images) {
      try {
        await downloadBlob(img.url, `ulubione_${img.zdjecie_id || done + 1}.${getFileExt(img.image_path)}`);
        done++; setDownloadProgress(done);
        await new Promise(r => setTimeout(r, 350));
      } catch {}
    }
    setDownloadingAll(false); setDownloadProgress(0);
    showToast(`Pobrano ${done} plików 🎉`);
  };

  const handleShare = async () => {
    let token = shareToken;
    if (!token) {
      try {
        const res = await api.post("/generate_share_token.php", { user_id: user.id });
        token = res.data.token; setShareToken(token);
      } catch { showToast("Nie udało się wygenerować linku.", "error"); return; }
    }
    const url = `${window.location.origin}/shared-favorites/${token}`;
    setShareLink(url); setShowShareBar(true);
    if (navigator.share) {
      try { await navigator.share({ title: "Moje ulubione – Księga Gości", url }); showToast("Udostępniono! 🎉"); return; }
      catch {}
    }
    try { await navigator.clipboard.writeText(url); showToast("Link skopiowany! 📋"); }
    catch { showToast("Link wygenerowany.", "info"); }
  };

  const openModal = (idx) => { setModalIdx(idx); setDirection(0); setModalOpen(true); };
  const showPrev = () => { if (modalIdx > 0) { setDirection(-1); setModalIdx(i => i - 1); } };
  const showNext = () => { if (modalIdx < images.length - 1) { setDirection(1); setModalIdx(i => i + 1); } };
  const handleTouchStart = (e) => { touchStartY.current = e.touches[0].clientY; };
  const handleTouchEnd = (e) => {
    if (touchStartY.current === null) return;
    const dy = e.changedTouches[0].clientY - touchStartY.current;
    if (dy > 55) showPrev(); else if (dy < -55) showNext();
    touchStartY.current = null;
  };
  const handleWheel = (e) => { if (e.deltaY > 0) showNext(); else if (e.deltaY < 0) showPrev(); };

  useEffect(() => {
    if (!modalOpen) return;
    const onKey = (e) => {
      if (e.key === "ArrowUp" || e.key === "ArrowLeft") showPrev();
      if (e.key === "ArrowDown" || e.key === "ArrowRight") showNext();
      if (e.key === "Escape") setModalOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [modalOpen, modalIdx, images.length]); // eslint-disable-line

  const currentImg = images[modalIdx];
  const currentIsVideo = isVideoFile(currentImg?.image_path);

  return (
    <Box sx={{ bgcolor: "#fff", minHeight: "100vh" }}>
      <Box sx={{ maxWidth: { xs: "100%", md: 935 }, mx: "auto" }}>

      {/* ── Header ── */}
      <Box sx={{ px: 2, py: 1.5, borderBottom: "1px solid #efefef", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography sx={{ fontWeight: 800, fontSize: 16, color: "#111" }}>Ulubione</Typography>
          {images.length > 0 && (
            <Box sx={{ bgcolor: "#EB578A", color: "#fff", borderRadius: 10, px: 1, py: 0.15, fontSize: 12, fontWeight: 700, lineHeight: 1.6 }}>
              {images.length}
            </Box>
          )}
        </Box>
        <Box sx={{ display: "flex", gap: 0.5 }}>
          {images.length > 0 && (
            <IconButton
              onClick={handleDownloadAll} disabled={downloadingAll} size="small"
              sx={{ color: "#111", "&.Mui-disabled": { opacity: 0.4 } }}
            >
              {downloadingAll
                ? <CircularProgress size={18} sx={{ color: "#EB578A" }} />
                : <DownloadForOfflineIcon />}
            </IconButton>
          )}
          <IconButton onClick={handleShare} size="small" sx={{ color: "#111" }}>
            <ShareIcon />
          </IconButton>
        </Box>
      </Box>

      {downloadingAll && (
        <LinearProgress variant="determinate" value={(downloadProgress / images.length) * 100}
          sx={{ height: 2, bgcolor: "#ffd6e7", "& .MuiLinearProgress-bar": { background: "linear-gradient(90deg,#EB578A,#C0145E)" } }} />
      )}

      {/* Share link bar */}
      {showShareBar && shareLink && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, bgcolor: "#fff0f5", px: 2, py: 1, borderBottom: "1px solid #ffd6e7" }}>
          <Typography sx={{ fontSize: 12, color: "#EB578A", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {shareLink}
          </Typography>
          <IconButton size="small" onClick={async () => { try { await navigator.clipboard.writeText(shareLink); showToast("Skopiowano! 📋"); } catch {} }}>
            <ContentCopyIcon sx={{ fontSize: 16, color: "#C0145E" }} />
          </IconButton>
          <IconButton size="small" onClick={() => setShowShareBar(false)}>
            <CloseIcon sx={{ fontSize: 16, color: "#888" }} />
          </IconButton>
        </Box>
      )}

      {/* ── Grid ── */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "50vh" }}>
          <CircularProgress sx={{ color: "#EB578A" }} />
        </Box>
      ) : images.length === 0 ? (
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "50vh", gap: 2 }}>
          <Box sx={{ width: 80, height: 80, borderRadius: "50%", border: "2px solid #efefef", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <FavoriteBorderIcon sx={{ fontSize: 36, color: "#ddd" }} />
          </Box>
          <Typography sx={{ fontWeight: 800, fontSize: 17, color: "#111" }}>Brak ulubionych</Typography>
          <Typography sx={{ color: "#aaa", fontSize: 14, textAlign: "center", px: 4 }}>
            Polub zdjęcia w galerii,{"\n"}a pojawią się tutaj ❤️
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "2px" }}>
          {images.map((img, idx) => {
            const isVid = isVideoFile(img.image_path);
            return (
              <Box
                key={img.zdjecie_id || idx}
                onClick={() => openModal(idx)}
                sx={{ aspectRatio: "1/1", overflow: "hidden", position: "relative", bgcolor: "#111", cursor: "pointer", contain: "layout style" }}
              >
                {isVid ? (
                  <>
                    <img src={getThumbnailUrl(img.image_path)} alt="" loading="lazy" draggable={false}
                      style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                      onError={e => {
                        const v = document.createElement("video");
                        v.src = `${img.url}#t=0.001`; v.preload = "metadata"; v.muted = true; v.playsInline = true;
                        Object.assign(v.style, { width: "100%", height: "100%", objectFit: "cover", display: "block" });
                        e.target.replaceWith(v);
                      }}
                    />
                    <Box sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", pointerEvents: "none" }}>
                      <PlayArrowIcon sx={{ color: "#fff", fontSize: 28, filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.5))" }} />
                    </Box>
                  </>
                ) : (
                  <img src={getThumbnailUrl(img.image_path)} alt="" loading="lazy" draggable={false}
                    style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                    onError={e => { e.target.src = img.url; }}
                  />
                )}
                {/* Heart indicator */}
                <FavoriteIcon sx={{ position: "absolute", top: 6, right: 6, fontSize: 14, color: "#EB578A", filter: "drop-shadow(0 1px 3px rgba(0,0,0,0.4))" }} />
              </Box>
            );
          })}
        </Box>
      )}

      </Box>{/* end maxWidth container */}

      {/* ── MODAL ── */}
      <Modal open={modalOpen} onClose={() => setModalOpen(false)}>
        <Box
          sx={{ position: "fixed", inset: 0, bgcolor: "#000", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1300 }}
          onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd} onWheel={handleWheel}
        >
          <IconButton onClick={() => setModalOpen(false)}
            sx={{ position: "fixed", top: "max(env(safe-area-inset-top),14px)", right: 14, zIndex: 20, color: "#fff", bgcolor: "rgba(0,0,0,0.45)", backdropFilter: "blur(8px)" }}>
            <CloseIcon />
          </IconButton>

          {!isMobile && modalIdx > 0 && (
            <Box onClick={e => { e.stopPropagation(); showPrev(); }}
              sx={{ position: "fixed", left: 16, top: "50%", transform: "translateY(-50%)", zIndex: 20, width: 44, height: 44, borderRadius: "50%", bgcolor: "rgba(255,255,255,0.15)", backdropFilter: "blur(8px)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "#fff", fontSize: 24, "&:hover": { bgcolor: "rgba(255,255,255,0.25)" }, userSelect: "none" }}>‹</Box>
          )}
          {!isMobile && modalIdx < images.length - 1 && (
            <Box onClick={e => { e.stopPropagation(); showNext(); }}
              sx={{ position: "fixed", right: 16, top: "50%", transform: "translateY(-50%)", zIndex: 20, width: 44, height: 44, borderRadius: "50%", bgcolor: "rgba(255,255,255,0.15)", backdropFilter: "blur(8px)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "#fff", fontSize: 24, "&:hover": { bgcolor: "rgba(255,255,255,0.25)" }, userSelect: "none" }}>›</Box>
          )}

          <AnimatePresence initial={false} custom={direction} mode="wait">
            <motion.div
              ref={modalRef} key={modalIdx}
              custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit"
              style={{ width: "100vw", height: "100vh", display: "flex", flexDirection: "column", background: "#000", position: "relative" }}
            >
              <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
                {currentImg && (currentIsVideo ? (
                  <video key={currentImg.url} src={currentImg.url} controls autoPlay playsInline
                    style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} />
                ) : (
                  <img src={currentImg.url} alt={currentImg.text || ""}
                    style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain", display: "block" }} draggable={false} />
                ))}
              </Box>

              {/* Bottom overlay */}
              <Box sx={{
                position: "absolute", bottom: 0, left: 0, right: 0,
                background: "linear-gradient(0deg, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.6) 50%, transparent 100%)",
                px: 2, pt: 6, pb: "calc(env(safe-area-inset-bottom) + 80px)", pointerEvents: "none",
              }}>
                {currentImg?.text && (
                  <Typography sx={{ color: "#fff", fontSize: 15, fontWeight: 600, mb: 1, lineHeight: 1.4 }}>
                    {currentImg.text}
                  </Typography>
                )}
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pointerEvents: "auto" }}>
                  <Typography sx={{ color: "rgba(255,255,255,0.5)", fontSize: 13 }}>{modalIdx + 1} / {images.length}</Typography>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <IconButton onClick={() => handleRemoveLike(currentImg)}
                      sx={{ bgcolor: "rgba(235,87,138,0.3)", backdropFilter: "blur(8px)", color: "#EB578A" }}>
                      <FavoriteIcon />
                    </IconButton>
                    <IconButton onClick={() => handleDownloadImage(currentImg)}
                      sx={{ bgcolor: "rgba(255,255,255,0.15)", backdropFilter: "blur(8px)", color: "#fff" }}>
                      <DownloadIcon />
                    </IconButton>
                  </Box>
                </Box>
              </Box>
            </motion.div>
          </AnimatePresence>
        </Box>
      </Modal>

      <Snackbar open={toast.open} autoHideDuration={2400} onClose={() => setToast(t => ({ ...t, open: false }))} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert severity={toast.severity} onClose={() => setToast(t => ({ ...t, open: false }))} sx={{ borderRadius: 3, fontWeight: 600 }}>
          {toast.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Favorites;
